import clsx from "classnames";
import { Container } from "reactstrap";
import styles from "./styles.module.scss";

import Arrow from "@/public/site-assets/svg/swiper-arrow.svg";

import { Autoplay, Navigation, Pagination } from "swiper";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";

import SF_TestimonialCard_002 from "@/components/Shared/SF_TestimonialCard_002";
import { motion } from "framer-motion";
import useTestimonials from "hooks/useTestimonials";
import { useEffect, useRef, useState } from "react";

import FooterWave2Icon from "@/public/site-assets/svg/footer-wave-2.svg";

export const LatestTestimonials = ({
  className,
  heading,
  content,
  swiperID = "latest_testimonials",
  xl,
  lg,
  md,
  sm,
  showWave,
  waveDirection,
  waveColor,
  waveBackground,
}) => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const post = useTestimonials();

  const settings = {
    modules: [Navigation, Autoplay, Pagination],
    loop: true,
    spaceBetween: 20,
    centeredSlides: false,
    slidesPerView: 1,
    autoHeight: false,
    speed: 500,
    // effect: "fade",
    // fadeEffect: { crossFade: true },
    autoplay: {
      delay: 8000,
    },
    navigation: {
      nextEl: `.${swiperID}.swiper-next`,
      prevEl: `.${swiperID}.swiper-prev`,
    },
    pagination: {
      el: `.${swiperID}.swiper-pagination`,
      clickable: true,
      renderBullet: (index, className) => {
        return `<span class="${className}">${index + 1}</span>`;
      },
    },

    breakpoints: {
      320: {
        slidesPerView: sm,
      },
      768: {
        slidesPerView: md,
      },
      991: {
        slidesPerView: lg,
      },
      1399: {
        slidesPerView: xl,
      },
    },
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1, // Adjust threshold as needed, make it smaller for more sensitive detection
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, options);

    observer.observe(sectionRef.current);

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobileDevice = window.innerWidth <= 991; // Adjust threshold for mobile devices
      // setIsVisible(!mobileDevice); // Hide motion on mobile devices
    };

    handleResize(); // Call initially
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section ref={sectionRef} className={clsx(className, styles.root)}>
      <Container>
        <div className="mb-2">
          <div className="d-lg-flex justify-content-between gap-4 align-items-center">
            <div className="d-lg-flex align-items-center gap-5 flex-wrap flex-lg-nowrap">
              {heading && <h3 className={`h3 heading`}>{heading}</h3>}
            </div>
            <div className="d-none d-lg-flex align-items-center">
              <button
                className={`${swiperID} swiper-prev border-0 bg-transparent`}
                aria-label="Prev"
              >
                <Arrow />
              </button>
              <button
                className={`${swiperID} swiper-next border-0 bg-transparent`}
                aria-label="Next"
              >
                <Arrow />
              </button>
            </div>
          </div>

          {content && <div className={`subtitle`} dangerouslySetInnerHTML={{ __html: content }} />}

          <div className="swiper-container">
            <Swiper {...settings}>
              {post?.map((postItem, index) => (
                <SwiperSlide key={index}>
                  <motion.div
                    className="h-100"
                    initial={{ opacity: 0, y: 50 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <SF_TestimonialCard_002
                      post={postItem}
                      pageTitle={`${postItem.title}`}
                      pageContent={`${postItem.content}`}
                      pageSubtitle={`${postItem.subtitle}`}
                      pagePosition={`${postItem.position}`}
                      postImage={`${postItem.image}`}
                      pageSector={`${postItem.sector}`}
                      pageLocation={`${postItem.location}`}
                    />
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div
              className={`${swiperID} swiper-pagination position-relative d-inline-flex d-lg-none justify-content-center`}
            />
          </div>
        </div>
      </Container>
      {showWave && (
        <div
          className={clsx(
            "footer",
            waveDirection === "down" && "rotate",
            waveColor,
            waveBackground,
          )}
        >
          <FooterWave2Icon />
        </div>
      )}
    </section>
  );
};

export default LatestTestimonials;
